import type { ComponentProps, HTMLAttributes } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import './Sheet.scss'

/**
 * Sheet — a slide-in overlay panel built on Radix Dialog.
 *
 * Unlike the centered `Dialog`, a Sheet enters from an edge (`side="top"` by
 * default) and carries an optional decorative "torn paper" wave edge. First
 * used by the Habits create/edit flow; reusable for future panels.
 *
 * Accessibility: always render a `SheetTitle` inside `SheetContent` (it may be
 * visually hidden) so screen readers announce the panel — Radix requires it.
 *
 * @example
 * <Sheet open={open} onOpenChange={setOpen}>
 *   <SheetContent side="top">
 *     <SheetTitle>Neue Gewohnheit festhalten</SheetTitle>
 *     …
 *   </SheetContent>
 * </Sheet>
 */
const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetPortal = DialogPrimitive.Portal
const SheetClose = DialogPrimitive.Close

function SheetOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return <DialogPrimitive.Overlay className={`sheet-overlay ${className || ''}`} {...props} />
}

interface SheetContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** Edge the sheet slides in from. Defaults to `top`. */
  side?: 'top' | 'bottom'
  /** Show the decorative torn-paper wave edge. Defaults to true. */
  showWave?: boolean
}

function SheetContent({ className, children, side = 'top', showWave = true, ...props }: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content className={`sheet sheet--${side} ${className || ''}`} {...props}>
        {showWave && <div className="sheet__wave" aria-hidden />}
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`sheet__header ${className || ''}`} {...props} />
}

function SheetFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`sheet__footer ${className || ''}`} {...props} />
}

function SheetTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={`sheet__title ${className || ''}`} {...props} />
}

function SheetDescription({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={`sheet__description ${className || ''}`} {...props} />
}

export {
  Sheet,
  SheetTrigger,
  SheetPortal,
  SheetClose,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
